import React, { useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Camera, ImagePlus, X, Send, Heart } from "lucide-react";
import { Chapter, MaskLines, Reveal } from "./Reveal";
import { HeartIcon } from "./Deco";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;
const MAX_BYTES = 10 * 1024 * 1024;

const Field = ({ label, children }) => (
  <div>
    <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--gold)] block mb-2">{label}</label>
    {children}
  </div>
);

export const Guestbook = ({ t }) => {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState([]); // { file, url }
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    let next = [...photos];
    for (const file of incoming) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > MAX_BYTES) {
        toast.error(t.tooBig);
        continue;
      }
      if (next.length >= MAX_PHOTOS) {
        toast.error(t.tooMany);
        break;
      }
      next.push({ file, url: URL.createObjectURL(file) });
    }
    setPhotos(next);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[idx].url);
      copy.splice(idx, 1);
      return copy;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t.required);
      return;
    }
    if (!message.trim() && photos.length === 0) {
      toast.error(t.needContent);
      return;
    }
    setSending(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("message", message.trim());
      photos.forEach((p) => fd.append("files", p.file, p.file.name));
      await axios.post(`${API}/guestbook`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setDone(true);
      toast.success(t.success);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.error);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setName("");
    setMessage("");
    setPhotos([]);
    setProgress(0);
    setDone(false);
  };

  return (
    <section id="guestbook" className="relative paper py-24 sm:py-32 px-5">
      <div className="max-w-xl mx-auto text-center">
        <Chapter>{t.chapter}</Chapter>
        <div className="flex items-center justify-center gap-3">
          <HeartIcon size={16} className="text-[var(--gold-dark)]" />
          <MaskLines lines={[t.title]} className="font-serif text-4xl sm:text-6xl text-[var(--forest)]" />
          <HeartIcon size={16} className="text-[var(--gold-dark)]" />
        </div>
        <Reveal delay={0.1}>
          <p className="font-sans text-sm text-[var(--olive)]/70 mt-4 max-w-md mx-auto">{t.note}</p>
        </Reveal>

        <Reveal delay={0.15}>
          {done ? (
            <div
              className="mt-12 rounded-2xl leather p-10 flex flex-col items-center"
              data-testid="guestbook-success"
            >
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 12 }}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] flex items-center justify-center mb-5"
              >
                <Heart size={26} className="text-[var(--forest)]" fill="currentColor" />
              </motion.div>
              <p className="gold-text font-serif text-2xl">{t.success}</p>
              <button
                data-testid="guestbook-again-btn"
                onClick={reset}
                className="mt-7 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[var(--gold)]/50 text-[var(--gold)] font-sans tracking-[0.18em] text-[11px] uppercase hover:bg-[var(--olive)]/40 transition-colors"
              >
                <ImagePlus size={14} /> {t.again}
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-12 rounded-2xl leather p-6 sm:p-9 text-left space-y-6 shadow-xl"
              data-testid="guestbook-form"
            >
              <Field label={t.name}>
                <input
                  data-testid="guestbook-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePh}
                  className="w-full bg-transparent border-b border-[var(--gold)]/40 py-2 font-serif text-lg text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors placeholder:text-[var(--gold-light)]/50"
                />
              </Field>

              <Field label={t.message}>
                <textarea
                  data-testid="guestbook-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t.messagePh}
                  rows={3}
                  className="w-full bg-transparent border-b border-[var(--gold)]/40 py-2 font-serif text-lg text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors resize-none placeholder:text-[var(--gold-light)]/50"
                />
              </Field>

              <Field label={`${t.photos}`}>
                <p className="font-sans text-[11px] text-[var(--gold-light)] -mt-1 mb-3">{t.photoHint}</p>
                <input
                  ref={inputRef}
                  data-testid="guestbook-file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  <AnimatePresence>
                    {photos.map((p, i) => (
                      <motion.div
                        key={p.url}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="relative aspect-square rounded-lg overflow-hidden border border-[var(--gold)]/40 group"
                        data-testid={`guestbook-thumb-${i}`}
                      >
                        <img src={p.url} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          data-testid={`guestbook-remove-${i}`}
                          onClick={() => removePhoto(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                          aria-label="remove"
                        >
                          <X size={13} />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      data-testid="guestbook-add-btn"
                      onClick={() => inputRef.current?.click()}
                      className="aspect-square rounded-lg border border-dashed border-[var(--gold)] flex flex-col items-center justify-center gap-1 text-[var(--gold-light)] hover:border-[var(--gold-light)] hover:text-[var(--gold-light)] transition-colors"
                    >
                      <Camera size={20} />
                      <span className="font-sans text-[9px] tracking-[0.15em] uppercase">{t.addPhotos}</span>
                    </button>
                  )}
                </div>
              </Field>

              {sending && (
                <div className="h-1.5 w-full rounded-full bg-[var(--olive)]/50 overflow-hidden" data-testid="guestbook-progress">
                  <div
                    className="h-full bg-gradient-to-r from-[#BF953F] to-[#FCF6BA] transition-[width] duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              <button
                data-testid="guestbook-submit"
                type="submit"
                disabled={sending}
                className="group w-full flex items-center justify-center gap-3 py-3.5 rounded-full font-sans tracking-[0.18em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                <Send size={15} /> {sending ? `${t.sending} ${progress}%` : t.submit}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
